import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, CreditCard, VideoCamera, ImageSquare, FilmStrip,
  UserCircle, ArrowsClockwise, Star, ChatCircle, TrendUp,
  Lightning, ShoppingCart, Crown
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="p-5 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}12` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.35)' }}>{label}</span>
    </div>
    <p className="text-2xl lg:text-3xl font-bold" style={{ color: '#f5f0e8', fontFamily: "'DM Sans'" }}>{value}</p>
    {sub && <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{sub}</p>}
  </div>
);

const FEATURE_LABELS = {
  video_ad: 'Video Ad', carousel: 'Carousel', flyer_generate: 'Flyer',
  character_generate: 'Character', voiceover: 'Voiceover', competitive_intel: 'Comp Intel',
  landing_page: 'Landing Page', landing_page_publish: 'LP Publish',
};

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [stripeEvents, setStripeEvents] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loadedTabs, setLoadedTabs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTab = useCallback(async (tabId) => {
    if (loadedTabs[tabId]) return;
    try {
      if (tabId === 'overview' && !stats) {
        const { data } = await axios.get(`${API}/api/admin/stats`);
        setStats(data);
      } else if (tabId === 'users' && users.length === 0) {
        const { data } = await axios.get(`${API}/api/admin/users`);
        setUsers(data.users || []);
      } else if (tabId === 'activity' && activity.length === 0) {
        const { data } = await axios.get(`${API}/api/admin/activity?limit=50`);
        setActivity(data.activity || []);
      } else if (tabId === 'feedback' && feedback.length === 0) {
        const { data } = await axios.get(`${API}/api/admin/feedback?limit=30`);
        setFeedback(data.feedback || []);
      } else if (tabId === 'subscribers' && subscribers.length === 0) {
        const { data } = await axios.get(`${API}/api/admin/subscribers`);
        setSubscribers(data.subscribers || []);
      } else if (tabId === 'purchases' && purchases.length === 0) {
        const { data } = await axios.get(`${API}/api/admin/purchases`);
        setPurchases(data.purchases || []);
        setStripeEvents(data.stripe_events || []);
      }
      setLoadedTabs(prev => ({ ...prev, [tabId]: true }));
    } catch (err) {
      if (err.response?.status === 403) setError('Admin access required.');
      else toast.error(`Failed to load ${tabId}`);
    }
  }, [loadedTabs, stats, users, activity, feedback, subscribers, purchases]);

  useEffect(() => {
    setLoading(true);
    loadTab('overview').finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTab(tab);
  }, [tab, loadTab]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <ArrowsClockwise size={24} className="animate-spin" style={{ color: '#c8f060' }} />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="py-20 text-center">
          <p className="text-sm" style={{ color: '#ff4444' }}>{error}</p>
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'subscribers', label: 'Subscribers' },
    { id: 'purchases', label: 'Purchases' },
    { id: 'activity', label: 'Activity' },
    { id: 'feedback', label: 'Feedback' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>Admin Dashboard</h1>
          <p className="text-sm mt-0.5" style={{ color: 'rgba(245,240,232,0.35)' }}>Monitor your platform, users, and revenue.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl overflow-x-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          {tabs.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-all text-center whitespace-nowrap px-3"
              style={{
                background: tab === t.id ? 'rgba(200,240,96,0.1)' : 'transparent',
                color: tab === t.id ? '#c8f060' : 'rgba(245,240,232,0.4)',
                border: tab === t.id ? '1px solid rgba(200,240,96,0.2)' : '1px solid transparent',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={Users} label="Total Users" value={stats.total_users} color="#c8f060" />
              <StatCard icon={CreditCard} label="Credits Used" value={stats.credits?.total_used || 0} sub={`${stats.credits?.recent_7d || 0} in last 7 days`} color="#f59e0b" />
              <StatCard icon={Star} label="Avg Rating" value={stats.feedback?.avg_rating || '—'} sub={`${stats.feedback?.total || 0} reviews`} color="#a78bfa" />
              <StatCard icon={TrendUp} label="Pro Users" value={stats.tier_counts?.pro || 0} sub={`${stats.tier_counts?.scale || 0} Scale`} color="#22d3ee" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={VideoCamera} label="Videos" value={stats.content?.videos || 0} color="#ff4444" />
              <StatCard icon={FilmStrip} label="Carousels" value={stats.content?.carousels || 0} color="#f59e0b" />
              <StatCard icon={ImageSquare} label="Flyers" value={stats.content?.flyers || 0} color="#c8f060" />
              <StatCard icon={UserCircle} label="Characters" value={stats.content?.characters || 0} color="#a78bfa" />
            </div>
            {stats.credits?.by_feature && Object.keys(stats.credits.by_feature).length > 0 && (
              <div className="p-5 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(245,240,232,0.3)' }}>Credits by Feature</p>
                <div className="space-y-2">
                  {Object.entries(stats.credits.by_feature).map(([feat, data]) => {
                    const maxCredits = Math.max(...Object.values(stats.credits.by_feature).map(d => d.credits));
                    return (
                      <div key={feat} className="flex items-center gap-3">
                        <span className="text-xs w-24 shrink-0" style={{ color: 'rgba(245,240,232,0.5)' }}>{FEATURE_LABELS[feat] || feat}</span>
                        <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <div className="h-full rounded-full transition-all" style={{
                            width: `${Math.max(5, (data.credits / maxCredits) * 100)}%`,
                            background: 'linear-gradient(90deg, rgba(200,240,96,0.3), rgba(200,240,96,0.6))',
                          }} />
                        </div>
                        <span className="text-xs w-16 text-right" style={{ color: '#c8f060' }}>{data.credits} cr</span>
                        <span className="text-[10px] w-12 text-right" style={{ color: 'rgba(245,240,232,0.25)' }}>{data.count}x</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users */}
        {tab === 'users' && (
          <div className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {['User', 'Tier', 'Credits', 'Onboarded', 'Joined', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium" style={{ color: '#f5f0e8' }}>{u.name || u.email}</p>
                        <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{
                          background: u.tier === 'scale' ? 'rgba(200,240,96,0.1)' : u.tier === 'pro' ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.05)',
                          color: u.tier === 'scale' ? '#c8f060' : u.tier === 'pro' ? '#f59e0b' : 'rgba(245,240,232,0.4)',
                        }}>{u.tier}</span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: '#f5f0e8' }}>{u.total_credits}</td>
                      <td className="px-4 py-3">
                        <div className="w-2 h-2 rounded-full" style={{ background: u.onboarding_completed ? '#c8f060' : 'rgba(255,255,255,0.1)' }} />
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {u.role !== 'admin' && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete ${u.email}? This cannot be undone. Their posts and brand pages will be removed.`)) return;
                              try {
                                const userId = u.user_id || u.id || u._id;
                                if (!userId) {
                                  // Fallback to legacy endpoint if id absent
                                  await axios.delete(`${API}/api/admin/user/${encodeURIComponent(u.email)}`);
                                } else {
                                  await axios.delete(`${API}/api/admin/users/${userId}`);
                                }
                                setUsers(prev => prev.filter(x => x.email !== u.email));
                                toast.success(`Deleted ${u.email}`);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete user'); }
                            }}
                            data-testid={`delete-user-${u.email}`}
                            className="text-[10px] px-2 py-1 rounded-md transition-all hover:bg-red-500/10"
                            style={{ color: 'rgba(255,68,68,0.6)', border: '1px solid rgba(255,68,68,0.15)' }}>
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Subscribers */}
        {tab === 'subscribers' && (
          <div className="space-y-4" data-testid="subscribers-tab">
            <div className="p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.1)' }}>
              <Crown size={20} style={{ color: '#c8f060' }} />
              <span className="text-sm font-medium" style={{ color: '#f5f0e8' }}>{subscribers.length} paid subscriber{subscribers.length !== 1 ? 's' : ''}</span>
            </div>
            {subscribers.length === 0 ? (
              <div className="py-12 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Crown size={32} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No paid subscribers yet.</p>
              </div>
            ) : (
              <div className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        {['Subscriber', 'Plan', 'Status', 'Monthly Used', 'Credits Left', 'Purchased', 'Since'].map(h => (
                          <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((s, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium" style={{ color: '#f5f0e8' }}>{s.name || s.email}</p>
                            <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{s.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{
                              background: s.tier === 'scale' ? 'rgba(200,240,96,0.12)' : 'rgba(245,158,11,0.12)',
                              color: s.tier === 'scale' ? '#c8f060' : '#f59e0b',
                            }}>{s.tier}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{
                              background: s.status === 'active' ? 'rgba(34,197,94,0.1)' : 'rgba(255,68,68,0.1)',
                              color: s.status === 'active' ? '#22c55e' : '#ff4444',
                            }}>{s.status}</span>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>{s.monthly_used || 0}</td>
                          <td className="px-4 py-3 text-sm font-medium" style={{ color: '#c8f060' }}>{s.monthly_credits || 0}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>{s.purchased_credits || 0}</td>
                          <td className="px-4 py-3 text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>
                            {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Purchases */}
        {tab === 'purchases' && (
          <div className="space-y-4" data-testid="purchases-tab">
            <div className="p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.1)' }}>
              <ShoppingCart size={20} style={{ color: '#f59e0b' }} />
              <span className="text-sm font-medium" style={{ color: '#f5f0e8' }}>{purchases.length} credit purchase{purchases.length !== 1 ? 's' : ''}</span>
            </div>
            {purchases.length === 0 && stripeEvents.length === 0 ? (
              <div className="py-12 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <ShoppingCart size={32} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No purchases yet.</p>
              </div>
            ) : (
              <>
                {purchases.length > 0 && (
                  <div className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Credit Transactions</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            {['User', 'Action', 'Credits', 'Balance After', 'Date'].map(h => (
                              <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {purchases.map((p, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                              <td className="px-4 py-3">
                                <p className="text-sm" style={{ color: '#f5f0e8' }}>{p.user_name || p.user_email || p.user_id}</p>
                              </td>
                              <td className="px-4 py-3 text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>{p.action || p.feature || '—'}</td>
                              <td className="px-4 py-3 text-sm font-medium" style={{ color: '#c8f060' }}>+{p.amount || p.cost || 0}</td>
                              <td className="px-4 py-3 text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>{p.balance_after ?? '—'}</td>
                              <td className="px-4 py-3 text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>
                                {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {stripeEvents.length > 0 && (
                  <div className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Stripe Events</p>
                    <div className="space-y-1 p-3">
                      {stripeEvents.map((ev, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                          <CreditCard size={14} style={{ color: '#f59e0b' }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs" style={{ color: '#f5f0e8' }}>{ev.type || ev.event_type || '—'}</p>
                            <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{ev.customer_email || ev.user_id || ''}</p>
                          </div>
                          <span className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
                            {ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Activity Feed */}
        {tab === 'activity' && (
          <div className="space-y-2">
            {activity.length === 0 ? (
              <div className="py-12 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Lightning size={32} style={{ color: 'rgba(245,240,232,0.1)' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No activity yet.</p>
              </div>
            ) : activity.map((tx, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.08)' }}>
                  <CreditCard size={14} style={{ color: '#c8f060' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate" style={{ color: '#f5f0e8' }}>
                    <span style={{ color: 'rgba(245,240,232,0.6)' }}>{tx.user_email || tx.user_id}</span>
                    {' used '}
                    <span className="font-medium" style={{ color: '#c8f060' }}>{tx.cost} credit{tx.cost !== 1 ? 's' : ''}</span>
                    {' for '}
                    <span className="font-medium">{FEATURE_LABELS[tx.feature] || tx.feature}</span>
                  </p>
                </div>
                <span className="text-[10px] shrink-0" style={{ color: 'rgba(245,240,232,0.2)' }}>
                  {tx.created_at ? new Date(tx.created_at).toLocaleString() : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Feedback */}
        {tab === 'feedback' && (
          <div className="space-y-3">
            {feedback.length === 0 ? (
              <div className="py-12 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <ChatCircle size={32} style={{ color: 'rgba(245,240,232,0.1)' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No feedback yet.</p>
              </div>
            ) : feedback.map((fb, i) => (
              <div key={i} className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium" style={{ color: 'rgba(245,240,232,0.5)' }}>{fb.user_email}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.3)' }}>{fb.category}</span>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={12} weight={s <= fb.rating ? 'fill' : 'regular'} style={{ color: s <= fb.rating ? '#f59e0b' : 'rgba(255,255,255,0.1)' }} />
                    ))}
                  </div>
                </div>
                {fb.message && <p className="text-sm" style={{ color: '#f5f0e8' }}>{fb.message}</p>}
                <p className="text-[10px] mt-2" style={{ color: 'rgba(245,240,232,0.2)' }}>
                  {fb.created_at ? new Date(fb.created_at).toLocaleString() : ''}
                  {fb.page && ` · ${fb.page}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
